import { z } from 'zod';

import { aiModelNameSchema } from './ai-models';
import { HarnessAgentNameSchema } from '../agent-harness/plan';
import {
  WorkflowAiCreateDraftToolCallResultSchema,
  WorkflowAiToolCallResultSchema,
  WorkflowCollectIntakeFormOutputSchema,
  WorkflowCollectLaunchInfoOutputSchema,
  WorkflowIdSchema,
} from '../workflow-ai';

// ============================================================================
// Tool Names
// ============================================================================

/**
 * Tool name enum-like const object.
 * These are the tool names used across the AI system.
 */
export const ALINE_TOOL_NAMES = {
  askUser: 'askUser',
  commentOnTaskTool: 'commentOnTask',
  completeTaskTool: 'completeTask',
  connectExternalAccount: 'connectExternalAccount',
  createReportTool: 'createReport',
  createSkillTool: 'createSkill',
  enableExternalTools: 'enableExternalTools',
  generatePlaybookTool: 'generatePlaybook',
  getFullDocumentTool: 'getFullDocumentTool',
  getTaskAgentThreadTool: 'getTaskAgentThread',
  getTaskTool: 'getTask',
  getWebPageContents: 'getWebPageContents',
  listTasksTool: 'listTasks',
  loadSkillTool: 'loadSkill',
  queryDocumentTool: 'queryDocument',
  readIntakeFormTool: 'readIntakeForm',
  readPlaybookTool: 'readPlaybook',
  readSkillResourceTool: 'readSkillResource',
  readSkillTool: 'readSkill',
  reassignTaskTool: 'reassignTask',
  runPlaybookTool: 'runPlaybook',
  searchPlaybooksTool: 'searchPlaybooks',
  searchSkillResourcesTool: 'searchSkillResources',
  searchSkillsTool: 'searchSkills',
  searchTool: 'searchTool',
  selectDocuments: 'selectDocuments',
  semanticSearch: 'semanticSearch',
  updateSkillTool: 'updateSkill',
  workflowAssignTask: 'workflowAssignTask',
  workflowCollectIntakeForm: 'workflowCollectIntakeForm',
  workflowCollectLaunchInfo: 'workflowCollectLaunchInfo',
  workflowCreateDraft: 'workflowCreateDraft',
  workflowLaunchTemplate: 'workflowLaunchTemplate',
  workflowManagePhases: 'workflowManagePhases',
  workflowReadIntakeForm: 'workflowReadIntakeForm',
  workflowReadResource: 'workflowReadResource',
  workflowSearchExisting: 'workflowSearchExisting',
  workflowSearchTemplates: 'workflowSearchTemplates',
  workflowUpdateIntakeForm: 'workflowUpdateIntakeForm',
} as const;

/**
 * Union type of all tool names.
 */
export type AlineToolName = (typeof ALINE_TOOL_NAMES)[keyof typeof ALINE_TOOL_NAMES];

// ============================================================================
// Tool Status
// ============================================================================

/**
 * Discriminated union for tool status.
 * A tool is either active or inactive with a reason.
 */
export type ToolStatusWithReason =
  | { readonly status: 'active' }
  | { readonly inactiveReason: string; readonly status: 'inactive' };

/**
 * Type-safe tool status map using mapped types.
 */
export type ToolStatusMap<T extends AlineToolName> = {
  readonly [K in T]: ToolStatusWithReason;
};

// ============================================================================
// Tool Call Schemas
// ============================================================================

/**
 * Note: This MUST be optional/nullable b/c prior messages in the system may not have a model name.
 */
const toolModelNameSchema = aiModelNameSchema.nullish();
const fallbackModelNameSchema = toolModelNameSchema.describe(
  'Only present if the system is in a "fallback" state.'
);

/**
 * The tool call.
 */
const calculatorToolCallSchema = z.object({
  expression: z.string(),
  id: z.string(),
  type: z.literal('calculator'),
});
/**
 * The kind of object an askUser option's id refers to. A closed set (not a free
 * string) so consumers can exhaustively handle each source and the model is
 * validated against known kinds; extend it when a new object becomes selectable.
 *
 * `value` is the one member that names no object: it marks a plain choice the
 * agent invented for the question (a negotiation stance, a side of an
 * agreement), whose id is the answer itself rather than a row to look up.
 * Without it a fixed-choice dropdown could only be expressed as a free-text
 * field.
 */
export const askUserOptionSourceSchema = z.enum([
  'document',
  'user',
  'value',
  'workflow',
  'workflowTemplate',
]);
export type AskUserOptionSource = z.infer<typeof askUserOptionSourceSchema>;

export const askUserOptionSchema = z.object({
  id: z
    .string()
    .min(1)
    .describe('Id of the selectable object (workflow, workflow template, document, or user).'),
  label: z.string().min(1).describe('User-facing name shown for this option.'),
  source: askUserOptionSourceSchema.describe(
    'Object kind for id: "workflow", "workflowTemplate", or "document" for a search result, "user" for an org member, or "value" for a plain choice you wrote yourself, where id is the answer text.'
  ),
});
export type AskUserOption = z.infer<typeof askUserOptionSchema>;

const askUserQuestionText = z
  .string()
  .min(1)
  .max(500)
  .describe('The concise question to ask the user.');
const askUserRequired = z
  .boolean()
  .optional()
  .describe(
    'Set true when the field is required — Submit stays disabled until it is answered. Optional fields may be left blank.'
  );
const askUserPlaceholder = z
  .string()
  .max(120)
  .optional()
  .describe('Optional short placeholder text for the user input.');
const askUserSuggestedValue = z
  .string()
  .optional()
  .describe(
    'A best-guess value to pre-fill from the context already provided (the conversation, attached documents, or the user profile). The field renders pre-filled and the user can edit or clear it. Leave empty when you cannot infer it.'
  );

/**
 * The answer recorded when the user skips a question. It is a non-empty string
 * so it survives answer filtering and reads to the resumed model as a
 * deliberate decline rather than a missing answer. The server reads it to tell
 * a skipped question from an answered one.
 */
export const ASK_USER_SKIPPED_ANSWER = '(skipped)';

/**
 * The askUser question, modeled as a union on `type` so illegal shapes are
 * unrepresentable: a file question can't also carry options, a text question
 * can't carry `maxFiles`, a date can't be marked `email`, etc. Shared as the
 * single source of truth by the server askUser tool's input schema and the
 * persisted tool-call schema below, so the two can never drift.
 *
 * Plain `z.union`, not `z.discriminatedUnion`. Zod emits JSON Schema `oneOf`
 * for a discriminated union, and OpenAI rejects that (`'oneOf' is not
 * permitted`). A plain union emits `anyOf`. Members still fix `type` to a
 * literal, so the inferred type narrows the same way.
 */
export const askUserQuestionSchema = z.union([
  z
    .object({
      placeholder: askUserPlaceholder,
      question: askUserQuestionText,
      required: askUserRequired,
      suggestedValue: askUserSuggestedValue,
      type: z.literal('text'),
    })
    .describe('A short free-text field.'),
  z
    .object({
      placeholder: askUserPlaceholder,
      question: askUserQuestionText,
      required: askUserRequired,
      suggestedValue: askUserSuggestedValue,
      type: z.literal('multiline'),
    })
    .describe('A long-text (textarea) field for a multi-line answer.'),
  z
    .object({
      placeholder: askUserPlaceholder,
      question: askUserQuestionText,
      required: askUserRequired,
      suggestedValue: askUserSuggestedValue,
      type: z.literal('number'),
    })
    .describe('A numeric field; the answer is validated as a number. Do not provide options.'),
  z
    .object({
      placeholder: askUserPlaceholder,
      question: askUserQuestionText,
      required: askUserRequired,
      suggestedValue: askUserSuggestedValue,
      type: z.literal('email'),
    })
    .describe(
      'An email field; the answer is validated as an email address. Do not provide options.'
    ),
  z
    .object({
      question: askUserQuestionText,
      required: askUserRequired,
      suggestedValue: z
        .string()
        .optional()
        .describe(
          'A best-guess date to pre-fill, as an ISO `YYYY-MM-DD` string. Leave empty when you cannot infer it.'
        ),
      type: z.literal('date'),
    })
    .describe(
      'A date field; the user gets a date picker and the answer is an ISO `YYYY-MM-DD` string.'
    ),
  z
    .object({
      options: z
        .array(askUserOptionSchema)
        .min(1)
        .max(10)
        .describe('The choices the user picks exactly one of.'),
      question: askUserQuestionText,
      required: askUserRequired,
      suggestedOptionId: z
        .string()
        .optional()
        .describe(
          'The option value to pre-select as your best guess. Renders pre-filled; the user can change it.'
        ),
      type: z.literal('select'),
    })
    .describe('A single-select field: the user picks exactly one of the provided options.'),
  z
    .object({
      options: z
        .array(askUserOptionSchema)
        .min(1)
        .max(10)
        .describe('The choices the user may pick more than one of.'),
      question: askUserQuestionText,
      required: askUserRequired,
      suggestedOptionIds: z
        .array(z.string())
        .optional()
        .describe(
          'The option values to pre-select as your best guess. Renders pre-filled; the user can change them.'
        ),
      type: z.literal('multiSelect'),
    })
    .describe('A multi-select (checklist) field: the user may pick more than one option.'),
  z
    .object({
      maxFiles: z
        .number()
        .int()
        .positive()
        .optional()
        .describe('Optional maximum number of files the user may upload.'),
      question: askUserQuestionText,
      required: askUserRequired,
      type: z.literal('file'),
    })
    .describe('A file-upload field: the user answers by uploading one or more documents.'),
  z
    .object({
      placeholder: askUserPlaceholder,
      question: askUserQuestionText,
      required: askUserRequired,
      type: z.literal('user'),
    })
    .describe(
      'An org-user picker: the client shows an autocomplete of active org members. Do not provide options — the UI hydrates them. The answer returns selectedOption with source "user" and the member\'s user id.'
    ),
]);

export type AskUserQuestionInput = z.infer<typeof askUserQuestionSchema>;

/**
 * Key under which the server stores the heading for an askUser card on the
 * persisted tool input, so the client can title the card after the thing the
 * agent is about to do ("AI wants to create a draft") rather than the generic
 * "Questions". Written by the agent that registers the tool, never by the
 * model, and absent for agents that want the default.
 */
export const ASK_USER_CARD_TITLE_INPUT_KEY = 'cardTitle';

const askUserToolCallSchema = z.object({
  id: z.string(),
  preparing: z.boolean().optional(),
  questions: z.array(askUserQuestionSchema).min(1).optional(),
  type: z.literal('askUser'),
});
const readDocumentToolCallSchema = z.object({
  documentId: z.string(),
  documentName: z.string(),
  id: z.string(),
  type: z.literal('readDocument'),
  /**
   * Set when the card is emitted while the document is still undergoing
   * OCR/text extraction, so the UI shows "waiting for processing" instead of
   * "reading" until it flips to finished.
   */
  waitingForExtraction: z.boolean().optional(),
});
const queryDocumentToolCallSchema = z
  .object({
    documentId: z.string(),
    documentName: z.string(),
    id: z.string(),
    /**
     * Legacy single-question shape kept for historical chat fences. Prefer
     * `retrievalQueries` + `rerankQuery`.
     */
    question: z.string().optional(),
    rerankQuery: z.string().min(1).optional(),
    retrievalQueries: z.array(z.string().min(1)).min(1).max(5).optional(),
    type: z.literal('queryDocument'),
    /** See `readDocumentToolCallSchema.waitingForExtraction`. */
    waitingForExtraction: z.boolean().optional(),
  })
  .refine(
    value =>
      Boolean(value.question?.trim()) ||
      (Boolean(value.retrievalQueries?.length) && Boolean(value.rerankQuery?.trim())),
    {
      message:
        'queryDocument requires retrievalQueries and rerankQuery, or a legacy question string',
    }
  );
const researchToolCallSchema = z.object({
  fallbackModel: fallbackModelNameSchema,
  id: z.string(),
  instructions: z.string(),
  originalModel: toolModelNameSchema,
  type: z.literal('research'),
});
const getWebPageContentsToolCallSchema = z.object({
  id: z.string(),
  type: z.literal('getWebPageContents'),
  urls: z.array(z.string()),
});
const searchToolCallSchema = z.object({
  id: z.string(),
  query: z.string(),
  type: z.literal('search'),
});
const searchPlaybooksToolCallSchema = z.object({
  id: z.string(),
  query: z.string(),
  type: z.literal('searchPlaybooks'),
});
/**
 * The card is emitted before the model has finished writing the plan, so every
 * field it learns later is optional. `preparing` is true only in that opening
 * moment; the documents arrive when the plan is put up for approval, and the
 * job and card ids only once the user has approved and generation has started.
 */
const generatePlaybookToolCallSchema = z.object({
  documentIds: z.array(z.string()).optional(),
  generationJobId: z.string().optional(),
  id: z.string(),
  playbookGenerationCardId: z.string().optional(),
  preparing: z.boolean().optional(),
  type: z.literal('generatePlaybook'),
});
/**
 * The card is emitted before the model has finished writing the plan, so every
 * field it learns later is optional. `preparing` is true only in that opening
 * moment; the name arrives when the plan is put up for approval, and the
 * report id only once the user has approved and the report exists.
 */
const createReportToolCallSchema = z.object({
  id: z.string(),
  name: z.string().optional(),
  preparing: z.boolean().optional(),
  reportId: z.string().optional(),
  type: z.literal('createReport'),
});
const readPlaybookToolCallSchema = z.object({
  id: z.string(),
  playbookId: z.string(),
  type: z.literal('readPlaybook'),
});
const loadSkillToolCallSchema = z.object({
  /** Set when the load was refused or failed, so the chip does not claim it loaded. */
  failed: z.boolean().optional(),
  id: z.string(),
  /** The skill's name, known once it has loaded. */
  name: z.string().optional(),
  skillId: z.string(),
  type: z.literal('loadSkill'),
  versionId: z.string(),
});
const readSkillResourceToolCallSchema = z.object({
  id: z.string(),
  path: z.string(),
  skillId: z.string(),
  type: z.literal('readSkillResource'),
  versionId: z.string(),
});
const searchSkillResourcesToolCallSchema = z.object({
  id: z.string(),
  query: z.string(),
  skillId: z.string(),
  type: z.literal('searchSkillResources'),
  versionId: z.string(),
});
const searchSkillsToolCallSchema = z.object({
  id: z.string(),
  query: z.string(),
  type: z.literal('searchSkills'),
});
const readSkillToolCallSchema = z.object({
  id: z.string(),
  skillId: z.string(),
  type: z.literal('readSkill'),
});
/**
 * The proposal starts while the model writes the skill. Its persisted tool output supplies
 * the saved skill id and final outcome; skillId remains readable for older messages.
 */
const SKILL_PROPOSAL_TOOL_CALL_FIELDS = {
  id: z.string(),
  name: z.string().optional(),
  preparing: z.boolean().optional(),
  skillId: z.string().optional(),
};
const createSkillToolCallSchema = z.object({
  ...SKILL_PROPOSAL_TOOL_CALL_FIELDS,
  type: z.literal('createSkill'),
});
const updateSkillToolCallSchema = z.object({
  ...SKILL_PROPOSAL_TOOL_CALL_FIELDS,
  type: z.literal('updateSkill'),
});
const runPlaybookToolCallSchema = z.object({
  documentId: z.string(),
  id: z.string(),
  playbookId: z.string(),
  playbookRunCardId: z.string(),
  type: z.literal('runPlaybook'),
});
const semanticSearchToolCallSchema = z
  .object({
    id: z.string(),
    /**
     * Legacy single-query shape kept for historical chat fences. Prefer
     * `retrievalQueries` + `rerankQuery`.
     */
    query: z.string().optional(),
    rerankQuery: z.string().min(1).optional(),
    retrievalQueries: z.array(z.string().min(1)).min(1).max(5).optional(),
    type: z.literal('semanticSearch'),
  })
  .refine(
    value =>
      Boolean(value.query?.trim()) ||
      (Boolean(value.retrievalQueries?.length) && Boolean(value.rerankQuery?.trim())),
    {
      message: 'semanticSearch requires retrievalQueries and rerankQuery, or a legacy query string',
    }
  );
const selectDocumentsToolCallSchema = z.object({
  id: z.string(),
  type: z.literal('selectDocuments'),
});
const getTaskToolCallSchema = z.object({
  id: z.string(),
  type: z.literal('getTask'),
});
const getTaskAgentThreadToolCallSchema = z.object({
  id: z.string(),
  type: z.literal('getTaskAgentThread'),
});
const readIntakeFormToolCallSchema = z.object({
  id: z.string(),
  type: z.literal('readIntakeForm'),
  workflowId: z.string(),
});
const updateIntakeFormToolCallSchema = z.object({
  id: z.string(),
  type: z.literal('updateIntakeForm'),
});
const commentOnTaskToolCallSchema = z.object({
  id: z.string(),
  type: z.literal('commentOnTask'),
});
// Retired tool; schema retained so historical chat messages keep parsing.
const updateTaskDescriptionToolCallSchema = z.object({
  id: z.string(),
  type: z.literal('updateTaskDescription'),
});
const listTasksToolCallSchema = z.object({
  id: z.string(),
  type: z.literal('listTasks'),
  workflowId: z.string(),
});
const reassignTaskToolCallSchema = z.object({
  id: z.string(),
  type: z.literal('reassignTask'),
});
const completeTaskToolCallSchema = z.object({
  id: z.string(),
  type: z.literal('completeTask'),
});
const compactPriorOutputToolCallSchema = z.object({
  id: z.string(),
  originalTokens: z.number(),
  sourceAgent: HarnessAgentNameSchema,
  sourceNodeId: z.number(),
  targetTokens: z.number(),
  type: z.literal('compactPriorOutput'),
});
const workflowCreateDraftToolCallSchema = z.object({
  id: z.string(),
  preparing: z.boolean().optional(),
  result: WorkflowAiCreateDraftToolCallResultSchema.optional(),
  type: z.literal('workflowCreateDraft'),
  workflowName: z.string(),
});
const workflowCollectIntakeFormToolCallSchema = z.object({
  id: z.string(),
  output: WorkflowCollectIntakeFormOutputSchema.optional(),
  templateWorkflowId: WorkflowIdSchema,
  type: z.literal('workflowCollectIntakeForm'),
});
const workflowCollectLaunchInfoToolCallSchema = z.object({
  id: z.string(),
  output: WorkflowCollectLaunchInfoOutputSchema.optional(),
  templateWorkflowId: WorkflowIdSchema,
  type: z.literal('workflowCollectLaunchInfo'),
});
const workflowLaunchTemplateToolCallSchema = z.object({
  id: z.string(),
  templateWorkflowId: z.string(),
  type: z.literal('workflowLaunchTemplate'),
  workflowName: z.string(),
});
/**
 * One batch of vendor tool calls made through an external connection.
 *
 * Deliberately absent from {@link ALINE_TOOL_NAMES}: that registry describes
 * tools the model may call, each carrying a description and an availability
 * status. This is a record of something that already happened, rendered in the
 * turn's history and never offered to a model.
 *
 * Recorded so the turn's visible history includes what it reached for outside
 * Aline — reading a mailbox is the kind of step a reader should see, not infer
 * from a document appearing. Carries the toolkit, the executed slugs, and
 * the criteria Aline searched on — never the vendor's response, which is the
 * half this record is rendered from but does not trust.
 */
const externalToolCallSchema = z.object({
  /**
   * What this batch searched for, drawn from the arguments Aline sent through
   * its own authorization layer — never from the vendor's response.
   *
   * Optional because most actions are not searches: reading a message by id has
   * no term to show, and an empty line reads worse than none.
   */
  criteria: z.string().min(1).optional(),
  id: z.string(),
  /**
   * What Aline verified out of the vendor result, patched in once
   * materialization finishes.
   *
   * Shown beside the step so a reader can check the assistant's prose against
   * what the server actually resolved instead of taking the narration on
   * trust — the prose is composed from a payload the message's sender wrote,
   * and this is not. Optional because the step is recorded before
   * materialization runs.
   */
  resolved: z
    .object({
      /** Aline documents created from this batch's attachments. */
      documentCount: z.number().int().min(0),
      /** Workspace members resolved from the envelope; never raw payload addresses. */
      userEmails: z.array(z.string().min(1)),
    })
    .optional(),
  /** Vendor tool slugs executed in this batch, e.g. `GMAIL_FETCH_EMAILS`. */
  toolSlugs: z.array(z.string().min(1)).min(1),
  /** Vendor toolkit slug, e.g. `gmail`. */
  toolkit: z.string().min(1),
  type: z.literal('externalTool'),
});
/**
 * The visible record of an offer to connect an account.
 *
 * Carries only the toolkit. The provider's authorization URL is minted when
 * the user actually clicks, not when this is recorded — the links expire in
 * minutes, so one issued at render time is usually dead by the time anyone
 * reads the card.
 */
const externalConnectToolCallSchema = z.object({
  id: z.string(),
  /** Vendor toolkit slug, e.g. `gmail`. */
  toolkit: z.string().min(1),
  type: z.literal('externalConnect'),
});
const workflowSearchExistingToolCallSchema = z.object({
  id: z.string(),
  rerankQuery: z.string().min(1),
  result: WorkflowAiToolCallResultSchema.optional(),
  retrievalQueries: z.array(z.string().min(1)).min(1).max(5),
  type: z.literal('workflowSearchExisting'),
});
const workflowSearchTemplatesToolCallSchema = z.object({
  id: z.string(),
  rerankQuery: z.string().min(1),
  result: WorkflowAiToolCallResultSchema.optional(),
  retrievalQueries: z.array(z.string().min(1)).min(1).max(5),
  type: z.literal('workflowSearchTemplates'),
});
const readWorkflowToolCallSchema = z.object({
  id: z.string(),
  type: z.literal('readWorkflow'),
  /** Optional for historical fences that only carried `{ id, type }`. */
  workflowId: z.string().optional(),
});
const managePhasesToolCallSchema = z.object({
  id: z.string(),
  operation: z.enum(['create', 'delete', 'reorder', 'update']),
  type: z.literal('managePhases'),
});
const assignTaskToolCallSchema = z.object({
  id: z.string(),
  /** Optional for historical fences that only carried `{ id, type }`. */
  taskId: z.string().optional(),
  type: z.literal('assignTask'),
  workflowId: z.string().optional(),
});
/**
 * Retained for backwards-compat parsing of historical AgentMessage rows
 * whose tool-call payloads carry these V1 agent names. The V1 code that
 * emitted them is gone; do not remove these values until those rows are
 * backfilled or aged out, otherwise loading old threads will throw.
 */
const agentNameSchema = z.enum([
  'draftDocument',
  'draftDocumentLongContext',
  'editSelection',
  'editSelectionLongContext',
  'orchestrator',
  'suggestEdits',
  'suggestEditsLongContext',
  'redline',
  'redlineLongContext',
  'documentAnalysis',
  'generalLegal',
  'workflow',
]);
const swapAgentToolCallSchema = z.object({
  fallbackModel: fallbackModelNameSchema,
  fromAgent: agentNameSchema,
  id: z.string(),
  originalModel: toolModelNameSchema,
  toAgent: agentNameSchema,
  type: z.literal('swapAgent'),
});
// This can use aiModelNameSchema (non-nullable) because it was introduced as part of the model fallback PR, which added original/fallbackModel fields to tool calls.
const modelFallbackToolCallSchema = z.object({
  fromModel: aiModelNameSchema,
  id: z.string(),
  toModel: aiModelNameSchema,
  type: z.literal('modelFallback'),
});
export const toolCallSchema = z.union([
  askUserToolCallSchema,
  calculatorToolCallSchema,
  getWebPageContentsToolCallSchema,
  queryDocumentToolCallSchema,
  readDocumentToolCallSchema,
  researchToolCallSchema,
  createReportToolCallSchema,
  generatePlaybookToolCallSchema,
  readPlaybookToolCallSchema,
  loadSkillToolCallSchema,
  readSkillResourceToolCallSchema,
  searchSkillResourcesToolCallSchema,
  runPlaybookToolCallSchema,
  searchToolCallSchema,
  searchPlaybooksToolCallSchema,
  searchSkillsToolCallSchema,
  readSkillToolCallSchema,
  createSkillToolCallSchema,
  updateSkillToolCallSchema,
  semanticSearchToolCallSchema,
  selectDocumentsToolCallSchema,
  getTaskToolCallSchema,
  getTaskAgentThreadToolCallSchema,
  readIntakeFormToolCallSchema,
  updateIntakeFormToolCallSchema,
  commentOnTaskToolCallSchema,
  updateTaskDescriptionToolCallSchema,
  listTasksToolCallSchema,
  reassignTaskToolCallSchema,
  completeTaskToolCallSchema,
  compactPriorOutputToolCallSchema,
  swapAgentToolCallSchema,
  modelFallbackToolCallSchema,
  workflowCollectIntakeFormToolCallSchema,
  workflowCollectLaunchInfoToolCallSchema,
  workflowCreateDraftToolCallSchema,
  workflowLaunchTemplateToolCallSchema,
  workflowSearchExistingToolCallSchema,
  workflowSearchTemplatesToolCallSchema,
  readWorkflowToolCallSchema,
  managePhasesToolCallSchema,
  assignTaskToolCallSchema,
  externalToolCallSchema,
  externalConnectToolCallSchema,
]);

export type ToolCall = z.infer<typeof toolCallSchema>;
export type ToolCallPatch<TToolCall extends ToolCall = ToolCall> = TToolCall extends ToolCall
  ? Partial<Omit<TToolCall, 'id' | 'type'>>
  : never;
export type AgentName = z.infer<typeof agentNameSchema>;
export type ToolCallType = ToolCall['type'];
export type SwapAgentToolCall = z.infer<typeof swapAgentToolCallSchema>;

export const toolCallTypeSchema = z.custom<ToolCallType>(getIsToolCallType);

const preparingToolCallFieldSchema = z.object({ preparing: z.boolean().optional() });

/**
 * Reads the placeholder flag off a tool call or a tool-call patch. Only a few
 * tool calls carry `preparing`, so the union has no shared field to read; this
 * answers "did this card open as a placeholder, leave that state, or say
 * nothing about it?" without narrowing on every member.
 */
export function getToolCallPreparing(value: unknown): boolean | undefined {
  const parsed = preparingToolCallFieldSchema.safeParse(value);
  return parsed.success ? parsed.data.preparing : undefined;
}

// ============================================================================
// Tool Call Status
// ============================================================================

/**
 * The status of a tool call.
 */
export const toolCallStatusValueSchema = z.enum(['processing', 'finished']);
export const toolCallStatusSchema = z.object({
  id: z.string(),
  status: toolCallStatusValueSchema,
});

export const agentMessageToolCallSchema = z.object({
  nodeId: z.number().int(),
  startedAt: z.string(),
  status: toolCallStatusValueSchema,
  toolCallType: toolCallTypeSchema,
});
export const agentMessageToolCallsSchema = z.record(z.string(), agentMessageToolCallSchema);

export type ToolCallStatus = z.infer<typeof toolCallStatusSchema>;
export type ToolCallStatusValue = z.infer<typeof toolCallStatusValueSchema>;
export type AgentMessageToolCall = z.infer<typeof agentMessageToolCallSchema>;
export type AgentMessageToolCalls = z.infer<typeof agentMessageToolCallsSchema>;

export function getIsAgentMessageToolCallProcessing(
  toolCalls: AgentMessageToolCalls | undefined,
  toolCallId: string
) {
  return toolCalls?.[toolCallId]?.status === 'processing';
}

function getIsToolCallType(value: unknown): value is ToolCallType {
  return toolCallSchema.options.some(schema => schema.shape.type.safeParse(value).success);
}
